import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import apiService from '../services/api';

interface Machine {
  id: number;
  number: string;
  groupId: number | null;
}

interface MachineGroup {
  id: number;
  name: string;
}

interface ShiftStartModalProps {
  employeeId: number;
  onClose: () => void;
  onStart: (machineIds: number[], machineGroupIds: number[]) => void;
}

const ShiftStartModal: React.FC<ShiftStartModalProps> = ({
  employeeId,
  onClose,
  onStart
}) => {
  const [selectedMachineIds, setSelectedMachineIds] = useState<number[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [machineGroups, setMachineGroups] = useState<MachineGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [machinesRes, groupsRes] = await Promise.all([
          apiService.get('/machines'),
          apiService.get('/machine-groups')
        ]);

        // Filter ungrouped machines
        const allMachines = machinesRes.machines || [];
        const ungroupedMachines = allMachines.filter((m: Machine) => !m.groupId);
        
        setMachines(ungroupedMachines);
        setMachineGroups(groupsRes.groups || []);
      } catch (error) {
        console.error('Error fetching machines and groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleMachine = (machineId: number) => {
    setSelectedMachineIds(prev =>
      prev.includes(machineId)
        ? prev.filter(id => id !== machineId)
        : [...prev, machineId]
    );
  };

  const toggleGroup = (groupId: number) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleStart = () => {
    if (selectedMachineIds.length === 0 && selectedGroupIds.length === 0) {
      alert('Please select at least one machine or machine group');
      return;
    }
    onStart(selectedMachineIds, selectedGroupIds);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Select Machines/Lines</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-4">
            Select the machines or machine groups (lines) you will be working on during this shift.
          </p>

          {/* Ungrouped Machines */}
          {machines.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Ungrouped Machines</h4>
              <div className="grid grid-cols-3 gap-2">
                {machines.map((machine) => (
                  <button
                    key={machine.id}
                    onClick={() => toggleMachine(machine.id)}
                    className={`p-3 border rounded text-left hover:bg-gray-50 ${
                      selectedMachineIds.includes(machine.id)
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{machine.number}</span>
                      {selectedMachineIds.includes(machine.id) && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Machine Groups */}
          {machineGroups.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Machine Groups (Lines)</h4>
              <div className="grid grid-cols-2 gap-2">
                {machineGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => toggleGroup(group.id)}
                    className={`p-3 border rounded text-left hover:bg-gray-50 ${
                      selectedGroupIds.includes(group.id)
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{group.name}</span>
                      {selectedGroupIds.includes(group.id) && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {machines.length === 0 && machineGroups.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">
              No machines or machine groups available.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={selectedMachineIds.length === 0 && selectedGroupIds.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Start Shift
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftStartModal;

